import React, { useState } from 'react';
import { TodoTree } from '../types/todo';
import { getTopTasks, getUnblockedTaskCount, ScoredTask } from '../utils/taskScoring';
import { TaskScoringConfig, DEFAULT_TASK_SCORING_CONFIG } from '../config/taskScoring';
import { getPriorityColor, getPriorityIcon } from '../utils/priority';

/**
 * Props for the TopTasks component
 */
interface TopTasksProps {
  /** The todo tree structure to analyze for top tasks */
  tree: TodoTree;
  /** Optional custom scoring configuration */
  config?: TaskScoringConfig;
}

/**
 * TopTasks - Displays the highest-priority actionable tasks
 *
 * This component analyzes the todo tree and ranks all leaf (actionable) tasks
 * by a calculated priority score. The scoring algorithm considers:
 * - Task priority level (low/medium/high/critical)
 * - Depth in the tree hierarchy (deeper = more specific)
 * - Parent priorities (inherited urgency)
 *
 * Features:
 * - Shows top 10 tasks with their calculated scores
 * - Collapsible panel to save screen space
 * - Color-coded priority icons
 * - Full path display to understand task context
 * - Only shows unblocked, incomplete tasks
 *
 * @example
 * ```tsx
 * <TopTasks tree={todoTree} />
 * ```
 */
const TopTasks: React.FC<TopTasksProps> = ({
  tree,
  config = DEFAULT_TASK_SCORING_CONFIG
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const topTasks = getTopTasks(tree, config);
  const unblockedCount = getUnblockedTaskCount(tree);
  const displayCount = Math.min(10, unblockedCount);

  // Don't render if no tasks
  if (topTasks.length === 0) {
    return null;
  }

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  const formatScore = (score: number): string => {
    return score.toFixed(2);
  };

  const renderColoredPath = (task: ScoredTask): React.ReactNode => {
    // Skip the root "Everything" node - use indices starting from 1
    const pathWithoutRoot = task.path.slice(1);
    const namesWithoutRoot = task.pathNames.slice(1);

    if (namesWithoutRoot.length <= 1) {
      return null; // Task name is already shown separately
    }

    return namesWithoutRoot.map((name, index) => {
      const itemId = pathWithoutRoot[index];
      const item = tree[itemId];
      const priority = item?.priority || 'low';
      const isLast = index === namesWithoutRoot.length - 1;

      return (
        <React.Fragment key={itemId}>
          <span style={{ color: getPriorityColor(priority) }}>
            {name}
          </span>
          {!isLast && <span className="path-separator"> › </span>}
        </React.Fragment>
      );
    });
  };

  return (
    <div className={`top-tasks ${isCollapsed ? 'collapsed' : ''}`}>
      <h4 className="top-tasks-title" onClick={toggleCollapsed}>
        <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>
          {isCollapsed ? '▶' : '▼'}
        </span>
        Top {displayCount} Tasks
        {unblockedCount > displayCount && (
          <span className="top-tasks-count"> (of {unblockedCount} unblocked)</span>
        )}
      </h4>

      {!isCollapsed && (
        <div className="top-tasks-list">
          {topTasks.map((task: ScoredTask, index: number) => (
            <div key={task.id} className="top-task-item">
              <div className="top-task-rank">{index + 1}</div>

              <div className="top-task-content">
                <div className="top-task-header">
                  <span
                    className="top-task-priority"
                    style={{ color: getPriorityColor(task.item.priority) }}
                    title={`Priority: ${task.item.priority}`}
                  >
                    {getPriorityIcon(task.item.priority)}
                  </span>

                  <span className="top-task-name">
                    {task.item.name}
                  </span>

                  <span className="top-task-score" title="Calculated priority score">
                    {formatScore(task.score)}
                  </span>
                </div>

                {task.pathNames.length > 1 && (
                  <div className="top-task-path">
                    {renderColoredPath(task)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopTasks;